// StoryAnimator.jsx
// Live ANIMATED 9:16 shift-recap Story preview, emulating the public-site hero:
// the shift's pancakes RISE from below the frame with gentle wobbles, fading in
// near the bottom and out past the top, with FairyDust sparkles drifting up over
// them — all behind the static text/stat overlay.
//
// Draws from ALL of the shift's pancakes (not the star-selection — starring is
// only for the static post). Rendered in a true 1080x1920 coordinate stage (so
// the overlay matches the server template exactly), CSS-scaled to `width`.
// Display-only: images load WITHOUT crossOrigin so they always render.

import { useEffect, useRef } from "react";
import { FONT } from "../theme/staff";
import FairyDustField from "../pages/site/components/FairyDustField";

const W = 1080, H = 1920;
const C = { gold: "#fcb31f", cream: "#ffdaad", sky: "#9fc4ec", white: "#ffffff", pillSub: "#cfe2f5" };

const SIZE_MIN = 365, SIZE_MAX = 480;   // floor raised ~40% — mostly big
const DUR = 20000;                      // UNIFORM rise — even phase-spacing holds = no gaps
const PEAK = 1.0;                       // basically solid — they ride behind the overlay
const TIERS = [
  { w: 2, size: 0.72, opa: 0.9, spd: 0.8, z: 1 },
  { w: 5, size: 1.0, opa: 1.0, spd: 1.0, z: 2 },
  { w: 3, size: 1.38, opa: 1.05, spd: 1.25, z: 3 },
];
function rnd(a, b) { return a + Math.random() * (b - a); }
function pickTier() { const tot = TIERS.reduce((s, t) => s + t.w, 0); let v = Math.random() * tot; for (const t of TIERS) { v -= t.w; if (v <= 0) return t; } return TIERS[1]; }
function opacityAt(t, peak) { if (t < 0.12) return (t / 0.12) * peak; if (t < 0.85) return peak; if (t < 1) return peak * 0.7 * (1 - (t - 0.85) / 0.15); return 0; }
// rise: 1.18h (below) → -0.2h (above), eased through keyframes (matches hero)
function yAt(t, h) {
  const s = [[0, 1.18], [0.12, 1.18 - 1.18 * 0.18], [0.5, 1.18 - 1.18 * 0.6], [0.85, 1.18 - 1.18 * 1.02], [1, -0.2]];
  for (let i = 1; i < s.length; i++) {
    if (t <= s[i][0]) { const [t0, y0] = s[i - 1], [t1, y1] = s[i]; const p = (t - t0) / (t1 - t0); return (y0 + (y1 - y0) * p) * h; }
  }
  return -0.2 * h;
}

// renderScale shrinks the canvas BACKING STORE (not the layout) — capture mode
// passes width/1080 so the per-frame raster cost (shadowBlur is the hot spot)
// scales with the capture resolution instead of always paying full 1080x1920.
// Canvas shadow params are device-space, so they're multiplied by the scale to
// keep the same visual proportions. Preview keeps the default full-res store.
export default function StoryAnimator({ width = 320, pancakes = [], overlay, showGuides = true, renderScale = 1 }) {
  const canvasRef = useRef(null);
  const driftersRef = useRef([]);
  const recentRef = useRef([]);
  const rafRef = useRef(null);

  const pool = pancakes.map((p) => p.thumbUrl || p.cutoutUrl).filter(Boolean);
  const poolKey = pool.join("|");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || pool.length === 0) return;
    const ctx = canvas.getContext("2d");
    const MAX = Math.min(7, Math.max(5, pool.length)); // keep the frame populated

    // 5 spawn columns; each new pancake enters ≥2 columns from the previous one
    // so they never bunch up. (Pigeonhole still allows reuse over time, which is
    // fine — they've risen away by then.)
    const COL_PCT = [18, 34, 50, 66, 82];
    let lastCol = -99;
    function pickColumnX() {
      let cands = COL_PCT.map((_, i) => i).filter((i) => Math.abs(i - lastCol) >= 2);
      if (!cands.length) cands = COL_PCT.map((_, i) => i);
      const ci = cands[Math.floor(Math.random() * cands.length)];
      lastCol = ci;
      return COL_PCT[ci] + rnd(-3, 3); // slight jitter so columns don't look rigid
    }

    function pickSrc(excludeSrcs) {
      const excl = new Set(excludeSrcs);
      recentRef.current.slice(0, Math.min(pool.length - 1, 4)).forEach((s) => excl.add(s));
      let src = pool.filter((s) => !excl.has(s));
      if (!src.length) src = pool.filter((s) => !excludeSrcs.includes(s));
      if (!src.length) src = pool;
      const chosen = src[Math.floor(Math.random() * src.length)];
      recentRef.current = [chosen, ...recentRef.current].slice(0, 8);
      return chosen;
    }
    function loadImage(url, onload) { const img = new Image(); img.onload = () => onload(img); img.onerror = () => onload(null); img.src = url; return img; }
    function makeDrifter(src, seedPhase) {
      const tier = pickTier();
      const size = rnd(SIZE_MIN, SIZE_MAX) * tier.size;
      const d = {
        src, img: null, size, dur: DUR,
        startX: pickColumnX(), driftX: rnd(-4, 4),
        startRot: rnd(-12, 12), driftRot: rnd(-16, 16),
        peak: PEAK * rnd(0.85, 1.1) * tier.opa,
        wobbleAmp: rnd(0.6, 1.8), wobblePeriod: rnd(15000, 24000), wobblePhase: rnd(0, Math.PI * 2),
        shadowMul: tier.size, z: tier.z,
        // seeds: spread evenly across the rise so the frame starts FULL (the 10s
        // attention window). recycles: re-enter at the bottom — uniform DUR means
        // the even spacing is preserved indefinitely, so no gaps ever open up.
        born: seedPhase != null ? performance.now() - seedPhase * DUR : performance.now() + rnd(0, 150),
        dead: false,
      };
      loadImage(src, (img) => { d.img = img; });
      return d;
    }

    const drifters = driftersRef.current;
    drifters.length = 0; recentRef.current = [];
    const used = [];
    for (let i = 0; i < MAX; i++) { const s = pickSrc(used); drifters.push(makeDrifter(s, (i + rnd(0, 0.25)) / MAX)); used.push(s); }

    function frame() {
      const now = performance.now();
      ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      ctx.clearRect(0, 0, W, H);
      drifters.sort((a, b) => a.z - b.z);
      for (const d of drifters) {
        if (d.dead) continue;
        const elapsed = now - d.born;
        if (elapsed < 0) continue;
        const t = elapsed / d.dur;
        if (t > 1) { d.dead = true; continue; }
        if (!d.img) continue;
        let alpha = opacityAt(t, d.peak);
        if (alpha < 0.004) continue;
        const xBase = (d.startX / 100) * W + (d.driftX / 100) * W * t;
        const wob = Math.sin((elapsed / d.wobblePeriod) * Math.PI * 2 + d.wobblePhase) * (d.wobbleAmp / 100) * W;
        const x = xBase + wob;
        const y = yAt(t, H);
        const rot = (d.startRot + d.driftRot * t) * Math.PI / 180;
        const iw = d.img.naturalWidth || d.img.width, ih = d.img.naturalHeight || d.img.height;
        if (!iw || !ih) continue;
        const scale = Math.min(d.size / iw, d.size / ih);
        const dw = iw * scale, dh = ih * scale;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(x, y);
        ctx.rotate(rot);
        ctx.shadowColor = `rgba(0,0,0,${0.4 * d.shadowMul})`;
        ctx.shadowBlur = 18 * d.shadowMul * renderScale;
        ctx.shadowOffsetY = 12 * d.shadowMul * renderScale;
        ctx.drawImage(d.img, -dw / 2, -dh / 2, dw, dh);
        ctx.restore();
      }
      // recycle dead
      for (let i = drifters.length - 1; i >= 0; i--) {
        if (!drifters[i].dead) continue;
        const dead = drifters[i];
        if (dead.img) { dead.img.src = ""; dead.img = null; }
        const active = drifters.filter((d) => !d.dead).map((d) => d.src);
        drifters[i] = makeDrifter(pickSrc(active), null);
      }
      rafRef.current = requestAnimationFrame(frame);
    }
    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      for (const d of drifters) { if (d.img) { d.img.src = ""; d.img = null; } }
      drifters.length = 0;
    };
  }, [poolKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const scale = width / W;
  const o = overlay || { l1: "", l2: "", date: "", count: 0, pills: [] };
  const l1Size = (o.l1 || "").length > 13 ? 52 : 64;
  const x0 = 64, gap = 20, total = W - 2 * x0, np = Math.max(1, o.pills.length), pw = (total - (np - 1) * gap) / np;
  const SAFE_TOP = 250, SAFE_BOTTOM = 1620; // IG Story chrome bands
  const abs = (s) => ({ position: "absolute", ...s });
  const txtShadow = "0 2px 14px rgba(0,0,0,0.55)";

  return (
    <div style={{ width, height: width * H / W, borderRadius: 14, overflow: "hidden", position: "relative", boxShadow: "0 8px 30px rgba(0,0,0,0.5)", flexShrink: 0 }}>
      <div style={{
        position: "absolute", top: 0, left: 0, width: W, height: H, transform: `scale(${scale})`, transformOrigin: "top left",
        backgroundColor: "#081a2a",
        backgroundImage: `radial-gradient(ellipse 92% 70% at 50% 100%, rgba(245,118,0,0.30) 0%, rgba(239,83,126,0.12) 45%, rgba(239,83,126,0) 72%), radial-gradient(ellipse 140% 95% at 50% 35%, #1f59db 0%, #1a4b9e 30%, #0e2e46 70%, #081a2a 100%)`,
        fontFamily: FONT.display, overflow: "hidden",
      }}>
        <canvas ref={canvasRef} width={Math.round(W * renderScale)} height={Math.round(H * renderScale)} style={{ position: "absolute", inset: 0, width: W, height: H, zIndex: 1 }} />
        {/* magic dust rising over the pancakes, like the hero */}
        <div style={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none" }}>
          <FairyDustField density={48} seed={7} riseDistance={760} />
        </div>

        {/* overlay text — coordinates match functions/social/templates/shiftStory.js
            and live inside the IG-safe band (top 250 / bottom 1620) */}
        <div style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none" }}>
          <div style={abs({ left: 64, top: 273, fontSize: 30, fontWeight: 600, letterSpacing: 5, color: C.gold, textShadow: txtShadow })}>◆ SHIFT RECAP</div>
          <div style={abs({ left: 64, top: 335, fontSize: l1Size, fontWeight: 700, color: C.white, lineHeight: 1, textShadow: txtShadow })}>{o.l1}</div>
          {o.l2 ? <div style={abs({ left: 64, top: 417, fontSize: 44, fontWeight: 600, color: C.cream, lineHeight: 1, textShadow: txtShadow })}>{o.l2}</div> : null}
          <div style={abs({ left: 64, top: 489, fontSize: 30, fontWeight: 400, color: C.sky, textShadow: txtShadow })}>{o.date}</div>
          <div style={abs({ right: 64, top: 263, fontSize: 172, fontWeight: 700, color: C.gold, lineHeight: 1, textShadow: txtShadow })}>{o.count}</div>
          <div style={abs({ right: 64, top: 453, fontSize: 32, fontWeight: 600, color: C.cream, textShadow: txtShadow })}>pancakes made live</div>
          {o.pills.map((p, i) => {
            const left = x0 + i * (pw + gap);
            return (
              <div key={i} style={abs({ left, top: 1392, width: pw, height: 132, borderRadius: 34, background: "rgba(8,26,42,0.55)", backdropFilter: "blur(2px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" })}>
                <div style={{ fontSize: (p.big || "").length > 9 ? 34 : 42, fontWeight: 700, color: p.gold ? C.gold : C.white }}>{p.big}</div>
                <div style={{ fontSize: 26, fontWeight: 500, color: C.pillSub, marginTop: 4 }}>{p.sub}</div>
              </div>
            );
          })}
          <div style={abs({ left: 0, top: 1570, width: W, textAlign: "center", fontSize: 32, fontWeight: 700, color: C.cream, opacity: 0.9, textShadow: txtShadow })}>The Joy of Pancakes · thejoyofpancakes.com</div>
        </div>

        {/* PREVIEW-ONLY safe-area guides (not in the exported asset) — show where
            Instagram's own UI sits so you can confirm nothing important is hidden */}
        {showGuides && <>
          <div style={{ position: "absolute", left: 0, top: 0, width: W, height: SAFE_TOP, zIndex: 4, background: "rgba(239,83,126,0.07)", borderBottom: "2px dashed rgba(239,83,126,0.45)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", left: 0, top: SAFE_BOTTOM, width: W, height: H - SAFE_BOTTOM, zIndex: 4, background: "rgba(239,83,126,0.07)", borderTop: "2px dashed rgba(239,83,126,0.45)", pointerEvents: "none" }} />
        </>}
      </div>
    </div>
  );
}
